import React, { useState } from 'react';
import {
  Box,
  Chip,
  Menu,
  MenuItem,
  Typography,
  Checkbox,
  ListItemText,
  Badge,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Build as BuildIcon,
  People as PeopleIcon,
  Category as CategoryIcon,
  Link as ChainIcon,
  LinkOff as BrokenChainIcon,
  Close as CloseIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';

const QuickFiltersBar = ({
  machines,
  employees,
  processTypes,
  activeFilters,
  onFilterChange,
  onClearAll,
}) => {
  const [machineMenuAnchor, setMachineMenuAnchor] = useState(null);
  const [employeeMenuAnchor, setEmployeeMenuAnchor] = useState(null);
  const [typeMenuAnchor, setTypeMenuAnchor] = useState(null);

  const toggleFilter = (category, value) => {
    const currentFilters = activeFilters[category] || [];
    const newFilters = currentFilters.includes(value)
      ? currentFilters.filter((v) => v !== value)
      : [...currentFilters, value];

    onFilterChange({ ...activeFilters, [category]: newFilters });
  };

  const hasActiveFilters = Object.values(activeFilters).some(
    (arr) => arr && arr.length > 0
  );

  const getTotalActiveFilters = () => {
    return Object.values(activeFilters).reduce(
      (sum, arr) => sum + (arr?.length || 0),
      0
    );
  };

  return (
    <Box className="quickFiltersBar">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <FilterListIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
        <Typography variant="body2" fontWeight={600} color="text.secondary">
          Filter:
        </Typography>
      </Box>

      {/* Machine Filter */}
      <Badge
        badgeContent={activeFilters.machines?.length || 0}
        color="primary"
        invisible={(activeFilters.machines?.length || 0) === 0}
      >
        <Chip
          icon={<BuildIcon />}
          label="Maschinen"
          onClick={(e) => setMachineMenuAnchor(e.currentTarget)}
          className={`filterChip ${
            (activeFilters.machines?.length || 0) > 0 ? 'active' : ''
          }`}
          variant={(activeFilters.machines?.length || 0) > 0 ? 'filled' : 'outlined'}
          color={(activeFilters.machines?.length || 0) > 0 ? 'primary' : 'default'}
        />
      </Badge>

      <Menu
        anchorEl={machineMenuAnchor}
        open={Boolean(machineMenuAnchor)}
        onClose={() => setMachineMenuAnchor(null)}
        PaperProps={{
          sx: { maxHeight: 400, width: 300 },
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            Maschinen auswählen
          </Typography>
        </Box>
        <Divider />
        {machines.map((machine) => (
          <MenuItem
            key={machine.id}
            onClick={() => toggleFilter('machines', machine.id)}
            dense
          >
            <Checkbox
              checked={(activeFilters.machines || []).includes(machine.id)}
              size="small"
            />
            <ListItemText primary={machine.label || machine.name} />
          </MenuItem>
        ))}
        {machines.length === 0 && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Keine Maschinen verfügbar
            </Typography>
          </Box>
        )}
      </Menu>

      {/* Employee Filter */}
      <Badge
        badgeContent={activeFilters.employees?.length || 0}
        color="primary"
        invisible={(activeFilters.employees?.length || 0) === 0}
      >
        <Chip
          icon={<PeopleIcon />}
          label="Mitarbeiter"
          onClick={(e) => setEmployeeMenuAnchor(e.currentTarget)}
          className={`filterChip ${
            (activeFilters.employees?.length || 0) > 0 ? 'active' : ''
          }`}
          variant={(activeFilters.employees?.length || 0) > 0 ? 'filled' : 'outlined'}
          color={(activeFilters.employees?.length || 0) > 0 ? 'primary' : 'default'}
        />
      </Badge>

      <Menu
        anchorEl={employeeMenuAnchor}
        open={Boolean(employeeMenuAnchor)}
        onClose={() => setEmployeeMenuAnchor(null)}
        PaperProps={{
          sx: { maxHeight: 400, width: 300 },
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            Mitarbeiter auswählen
          </Typography>
        </Box>
        <Divider />
        {employees.map((employee) => (
          <MenuItem
            key={employee.id}
            onClick={() => toggleFilter('employees', employee.id)}
            dense
          >
            <Checkbox
              checked={(activeFilters.employees || []).includes(employee.id)}
              size="small"
            />
            <ListItemText primary={employee.name || employee.label} />
          </MenuItem>
        ))}
        {employees.length === 0 && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Keine Mitarbeiter verfügbar
            </Typography>
          </Box>
        )}
      </Menu>

      {/* Process Type Filter */}
      {processTypes && processTypes.length > 0 && (
        <>
          <Badge
            badgeContent={activeFilters.processTypes?.length || 0}
            color="primary"
            invisible={(activeFilters.processTypes?.length || 0) === 0}
          >
            <Chip
              icon={<CategoryIcon />}
              label="Prozesstyp"
              onClick={(e) => setTypeMenuAnchor(e.currentTarget)}
              className={`filterChip ${
                (activeFilters.processTypes?.length || 0) > 0 ? 'active' : ''
              }`}
              variant={
                (activeFilters.processTypes?.length || 0) > 0 ? 'filled' : 'outlined'
              }
              color={
                (activeFilters.processTypes?.length || 0) > 0 ? 'primary' : 'default'
              }
            />
          </Badge>

          <Menu
            anchorEl={typeMenuAnchor}
            open={Boolean(typeMenuAnchor)}
            onClose={() => setTypeMenuAnchor(null)}
            PaperProps={{
              sx: { maxHeight: 400, width: 300 },
            }}
          >
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                Prozesstyp auswählen
              </Typography>
            </Box>
            <Divider />
            {processTypes.map((type) => (
              <MenuItem
                key={type.value}
                onClick={() => toggleFilter('processTypes', type.value)}
                dense
              >
                <Checkbox
                  checked={(activeFilters.processTypes || []).includes(type.value)}
                  size="small"
                />
                <ListItemText primary={type.label} />
              </MenuItem>
            ))}
          </Menu>
        </>
      )}

      {/* Chain Status Filters */}
      <Chip
        icon={<ChainIcon />}
        label="Verkettet"
        onClick={() => {
          const currentStatus = activeFilters.chainStatus || [];
          const newStatus = currentStatus.includes('chained')
            ? currentStatus.filter((s) => s !== 'chained')
            : [...currentStatus, 'chained'];
          onFilterChange({ ...activeFilters, chainStatus: newStatus });
        }}
        className={`filterChip ${
          (activeFilters.chainStatus || []).includes('chained') ? 'active' : ''
        }`}
        variant={
          (activeFilters.chainStatus || []).includes('chained') ? 'filled' : 'outlined'
        }
        color={
          (activeFilters.chainStatus || []).includes('chained') ? 'primary' : 'default'
        }
      />

      <Chip
        icon={<BrokenChainIcon />}
        label="Gebrochen"
        onClick={() => {
          const currentStatus = activeFilters.chainStatus || [];
          const newStatus = currentStatus.includes('broken')
            ? currentStatus.filter((s) => s !== 'broken')
            : [...currentStatus, 'broken'];
          onFilterChange({ ...activeFilters, chainStatus: newStatus });
        }}
        className={`filterChip ${
          (activeFilters.chainStatus || []).includes('broken') ? 'active' : ''
        }`}
        variant={
          (activeFilters.chainStatus || []).includes('broken') ? 'filled' : 'outlined'
        }
        color={
          (activeFilters.chainStatus || []).includes('broken') ? 'error' : 'default'
        }
      />

      {/* Clear All Button */}
      {hasActiveFilters && (
        <>
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          <Tooltip title="Alle Filter löschen">
            <Chip
              label={`${getTotalActiveFilters()} Filter aktiv`}
              onDelete={onClearAll}
              deleteIcon={<CloseIcon />}
              color="error"
              variant="outlined"
              size="small"
              className="clearAllFilters"
            />
          </Tooltip>
        </>
      )}
    </Box>
  );
};

export default QuickFiltersBar;
